import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { userService } from "@services/UserServices";
import { performRedirect } from "@utils/redirect";

/**
 * Custom hook to automatically log a user in using a token found in the URL.
 *
 * This hook extracts a `token` query parameter from the URL and attempts to
 * log the user in by calling the `loginViaToken` service. If successful, it
 * sets a cookie with the token from backend and reloads the page. If the login fails, it
 * calls the `setError` callback with an error message.
 *
 * @param setError - Callback to display an error message if token login fails.
 */
export const useTokenLoginRedirect = () => {
  // Get current location object to extract query params from URL
  const location = useLocation();

  // Navigation hook for potential redirects (not used currently)
  const navigate = useNavigate();

  // Ref to prevent the effect from running multiple times unnecessarily
  const effectRan = useRef(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const redirectParam = searchParams.get("redirect");

  const redirectPath = redirectParam ? decodeURIComponent(redirectParam) : null;

  useEffect(() => {
    // Prevent the effect from executing more than once (in development it may double-run)
    if (effectRan.current) return;
    effectRan.current = true;

    // Function to perform login using the token
    const loginUserViaToken = async () => {
      // If no token is present, do nothing
      if (!token) return;

      try {
        // Attempt login using the token
        const response = await userService.loginViaToken(token);

        // If login was successful (status 200)
        if (response?.status === 200) {
          setTimeout(() => {
            performRedirect("/assistant", redirectPath, navigate);
          }, 250);
        }
      } catch (error) {
        // If login failed, inform the caller using the setError callback
        navigate("/login");
      }
    };

    // Call the login function
    loginUserViaToken();
  }, [location, navigate, token, redirectPath]); // Run the effect when location or navigate changes
};
